import { WellScene, levelToFraction, prefersReducedMotion } from "../well-scene.js";
import { DemoSim, SIM } from "../demo-sim.js";
import { CONFIG } from "../config.js";
import { fetchLaunches, fetchJsonMeta, chainNow, currentFeeBps, priceSol, marketCapSol, curveProgress } from "../chain.js";
import { $, esc, fmtSol, fmtPrice, fmtNum, fmtAge, fmtBps, safeUrl, placeholderArt, short } from "../ui.js";
import { describeError } from "../errors.js";

export function mountHome(root) {
  root.innerHTML = `
  <section class="hero">
    <div class="hero-copy">
      <p class="eyebrow eyebrow-lines"><span>A Solana launchpad</span></p>
      <h1 class="display">Every trade<br/><span class="glow">fills the well.</span></h1>
      <p class="lede">Launch a token in one signature. A slice of every buy and sell drips into its well as SOL,
      and the holders who lower their tokens into the well drink it, block by block.</p>
      <div class="hero-cta">
        <a class="btn btn-primary btn-lg" href="#/create">Dig a new well</a>
        <a class="btn btn-ghost btn-lg" href="#/how">How the water flows</a>
      </div>
      <ul class="hero-facts" aria-label="Key rules">
        <li><b>15% → 4%</b><span>anti-sniper fee, first 60 s</span></li>
        <li><b>3%</b><span>of every trade into the well</span></li>
        <li><b>∞</b><span>one curve, no migration</span></li>
      </ul>
    </div>
    <div class="hero-stage" id="hero-stage">
      <canvas class="well-canvas hero-canvas" aria-label="The great well. Its water level shows the SOL held across all wells." role="img"></canvas>
      <div class="hero-gauge">
        <div class="gauge-label">Water in all wells</div>
        <div class="gauge-value"><span id="total-level">-</span> <small>SOL</small></div>
        <div class="gauge-sub"><span id="total-poured">-</span> SOL poured all-time · <span id="total-launches">-</span> wells</div>
      </div>
    </div>
  </section>

  <section class="launches">
    <div class="section-head">
      <div>
        <p class="eyebrow">Live on Solana ${CONFIG.cluster === "devnet" ? "devnet" : "mainnet"}</p>
        <h2 class="display-sm">Wells</h2>
      </div>
      <div class="controls" id="grid-controls" hidden>
        <div class="tabs" role="tablist" aria-label="Sort wells">
          <button role="tab" class="tab active" data-sort="new" aria-selected="true">Newest</button>
          <button role="tab" class="tab" data-sort="deep" aria-selected="false">Deepest well</button>
          <button role="tab" class="tab" data-sort="hot" aria-selected="false">Last trade</button>
        </div>
        <input class="search" id="search" type="search" placeholder="Search name, ticker or mint" aria-label="Search wells" />
      </div>
    </div>
    <div id="grid" class="grid" aria-live="polite">
      ${Array.from({ length: 3 }, () => `<div class="card skeleton"></div>`).join("")}
    </div>
  </section>

  <section class="sim" aria-labelledby="sim-title">
    <div class="section-head">
      <div>
        <p class="eyebrow">Live simulation</p>
        <h2 class="display-sm" id="sim-title">How the water moves</h2>
      </div>
      <span class="demo-badge demo-badge-lg" title="Everything in this section is simulated. No real tokens, trades or wallets.">Simulation · not real trades</span>
    </div>
    <div class="sim-wrap">
      <div class="sim-stage panel">
        <span class="demo-badge sim-corner">Demo</span>
        <canvas class="sim-canvas" role="img" aria-label="Simulation: drops of fee water fall into a demo well, the level rises, then the water flows out to four staked holders in proportion to their stake."></canvas>
      </div>
      <aside class="sim-side">
        <div class="panel sim-fee" id="sim-fee">
          <div class="sim-card-head"><span class="k">Anti-sniper fee</span><span class="demo-badge">Sim</span></div>
          <div class="sim-fee-body">
            <svg class="fee-ring" viewBox="0 0 100 100" aria-hidden="true">
              <circle cx="50" cy="50" r="42" class="ring-track" />
              <circle cx="50" cy="50" r="42" class="ring-fill" id="sim-ring" pathLength="100" />
            </svg>
            <div>
              <div class="sim-fee-now mono" id="sim-fee-now">15.0%</div>
              <div class="muted small" id="sim-fee-sub">0 s after a simulated launch</div>
            </div>
          </div>
          <svg class="fee-curve" viewBox="0 0 200 56" preserveAspectRatio="none" aria-hidden="true">
            <polyline class="fee-curve-line" points="0,6 ${((200 * SIM.decay) / SIM.loop).toFixed(1)},50 200,50" />
            <line class="fee-curve-now" id="sim-curve-now" x1="0" x2="0" y1="0" y2="56" />
            <circle class="fee-curve-dot" id="sim-curve-dot" cx="0" cy="6" r="3.5" />
          </svg>
          <div class="fee-curve-axis muted"><span>launch</span><span>60 s</span></div>
        </div>
        <div class="panel sim-stats">
          <div class="sim-card-head"><span class="k">This demo well</span><span class="demo-badge">Sim</span></div>
          <div class="sim-stat-grid">
            <div><div class="k">Level</div><div class="v mono cyan" id="sim-level">0.000</div></div>
            <div><div class="k">Poured in</div><div class="v mono" id="sim-poured">0.000</div></div>
            <div><div class="k">Drunk by holders</div><div class="v mono" id="sim-drunk">0.000</div></div>
            <div><div class="k">Demo trades</div><div class="v mono" id="sim-trades">0</div></div>
          </div>
          <ol class="sim-log" id="sim-log" aria-label="Simulated trade log"></ol>
        </div>
      </aside>
    </div>
    <p class="sim-caption"><span class="demo-badge">Simulation</span>
      A loop of the on-chain rules with made-up numbers in SOL: no real tokens, trades or wallets. Each drop is a simulated trade's fee
      (amber while the anti-sniper fee is high, cyan at the normal 4%); 1% goes to the platform and the rest falls into the well.
      The well then drains to staked holders pro-rata to their stake. Time is compressed: on chain the drain has about a one-hour time constant.
    </p>
  </section>`;

  const canvas = $(".hero-canvas", root);
  const scene = new WellScene(canvas, { hero: true, ambientDrips: true, initialLevel: 0.2, seed: 7 });
  const grid = $("#grid", root);
  const controls = $("#grid-controls", root);
  const sim = mountSim(root);
  const offParallax = heroParallax($("#hero-stage", root));
  if (/\?sim/.test(location.hash)) setTimeout(() => $(".sim", root)?.scrollIntoView({ behavior: "smooth", block: "start" }), 700);
  let launches = [];
  let sort = "new";
  let query = "";
  let lastTotal = null;
  let stopped = false;
  let pollTimer;

  root.querySelectorAll(".tab").forEach((t) =>
    t.addEventListener("click", () => {
      sort = t.dataset.sort;
      root.querySelectorAll(".tab").forEach((x) => {
        x.classList.toggle("active", x === t);
        x.setAttribute("aria-selected", x === t ? "true" : "false");
      });
      render();
    })
  );
  $("#search", root).addEventListener("input", (e) => {
    query = e.target.value.trim().toLowerCase();
    render();
  });

  function sorted() {
    let list = launches.slice();
    if (query) {
      list = list.filter((l) =>
        [l.meta?.name, l.meta?.symbol, l.mint.toBase58()].some((s) => (s || "").toLowerCase().includes(query))
      );
    }
    if (sort === "deep") list.sort((a, b) => (b.well.level > a.well.level ? 1 : b.well.level < a.well.level ? -1 : 0));
    else if (sort === "hot") list.sort((a, b) => b.curve.lastTradeTs - a.curve.lastTradeTs);
    else list.sort((a, b) => Number(b.launch.index - a.launch.index));
    return list;
  }

  function cardHtml(l) {
    const mint = l.mint.toBase58();
    const name = l.meta?.name || "Unnamed";
    const sym = l.meta?.symbol || "???";
    const lvlSol = Number(l.well.level) / 1e9;
    const fill = Math.round(levelToFraction(lvlSol, 20) * 100);
    const prog = curveProgress(l.curve);
    return `
    <a class="card" href="#/token/${esc(mint)}" data-mint="${esc(mint)}">
      <div class="card-top">
        <img class="card-img" alt="" loading="lazy" src="${placeholderArt(mint, sym)}" data-uri="${esc(l.meta?.uri || "")}" />
        <div class="card-title">
          <div class="card-name">${esc(name)}</div>
          <div class="card-sym">$${esc(sym)}</div>
        </div>
        <span class="fee-badge" data-fee></span>
      </div>
      <div class="card-well" title="Well level">
        <div class="mini-well"><div class="mini-water" style="height:${fill}%"></div></div>
        <div>
          <div class="k">Well level</div>
          <div class="v mono">${fmtSol(l.well.level, 4)} SOL</div>
        </div>
      </div>
      <div class="card-stats">
        <div><div class="k">Price</div><div class="v mono">${fmtPrice(priceSol(l.curve))}</div></div>
        <div><div class="k">Mcap</div><div class="v mono">${fmtNum(marketCapSol(l.curve), 1)} SOL</div></div>
        <div><div class="k">Age</div><div class="v mono" data-age>${fmtAge(chainNow() - l.launch.launchTimestamp)}</div></div>
      </div>
      <div class="progress" title="Curve progress: ${(prog * 100).toFixed(2)}% of supply sold">
        <div class="progress-bar" style="width:${Math.max(0.5, prog * 100).toFixed(2)}%"></div>
      </div>
      <div class="card-foot muted">by ${esc(short(l.launch.creator))} · ${Number(l.curve.buyCount + l.curve.sellCount)} trades</div>
    </a>`;
  }

  function render() {
    if (!launches.length) return;
    const list = sorted();
    if (!list.length) {
      grid.innerHTML = `<div class="empty">No wells match “${esc(query)}”.</div>`;
      return;
    }
    grid.innerHTML = list.map(cardHtml).join("");
    for (const img of grid.querySelectorAll("img[data-uri]")) {
      const uri = img.dataset.uri;
      if (!uri) continue;
      fetchJsonMeta(uri).then((j) => {
        const src = safeUrl(j?.image);
        if (src && img.isConnected) {
          const pre = new Image();
          pre.onload = () => img.isConnected && (img.src = src);
          pre.src = src;
        }
      });
    }
    tickBadges();
  }

  function tickBadges() {
    const now = chainNow();
    for (const card of grid.querySelectorAll(".card[data-mint]")) {
      const l = launches.find((x) => x.mint.toBase58() === card.dataset.mint);
      if (!l) continue;
      const fee = currentFeeBps(l.launch, now);
      const badge = card.querySelector("[data-fee]");
      const open = now - l.launch.launchTimestamp < l.launch.decaySeconds && fee > l.launch.baseFeeBps;
      badge.className = open ? "fee-badge hot" : "fee-badge";
      badge.textContent = open ? `Anti-sniper ${fmtBps(fee)}` : `Fee ${fmtBps(fee)}`;
      const age = card.querySelector("[data-age]");
      if (age) age.textContent = fmtAge(now - l.launch.launchTimestamp);
    }
  }

  async function load() {
    try {
      const { config, launches: ls } = await fetchLaunches(60);
      if (stopped) return;
      if (!config) {
        controls.hidden = true;
        grid.innerHTML = emptyStateHtml("soon");
        for (const id of ["#total-level", "#total-poured", "#total-launches"]) $(id, root).textContent = "0";
        scene.setLevel(0.08);
        return;
      }
      const changed =
        ls.length !== launches.length ||
        ls.some((l, i) => !launches[i] || launches[i].well.waterTotal !== l.well.waterTotal || launches[i].curve.virtualSol !== l.curve.virtualSol);
      launches = ls;
      const totalLevel = ls.reduce((s, l) => s + l.well.level, 0n);
      const totalPoured = ls.reduce((s, l) => s + l.well.waterTotal, 0n);
      $("#total-level", root).textContent = fmtSol(totalLevel, 4);
      $("#total-poured", root).textContent = fmtSol(totalPoured, 3);
      $("#total-launches", root).textContent = String(config.launchCount);
      const frac = levelToFraction(Number(totalLevel) / 1e9, 500);
      if (lastTotal !== null && totalPoured > lastTotal) {
        // new water arrived since last poll: let it fall in, then raise the level
        const n = Math.min(4, 1 + Math.floor(Number(totalPoured - lastTotal) / 1e8));
        for (let i = 0; i < n; i++) setTimeout(() => scene.drop({ size: 1.2, onImpact: () => scene.setLevel(frac) }), i * 260);
      } else {
        scene.setLevel(frac, { instant: lastTotal === null });
      }
      lastTotal = totalPoured;
      controls.hidden = !ls.length;
      if (!ls.length) {
        const mode = config.launchCount > 0n ? "older" : "fresh";
        if (grid.dataset.empty !== mode) grid.innerHTML = emptyStateHtml(mode);
        grid.dataset.empty = mode;
      } else if (changed || grid.dataset.empty) {
        delete grid.dataset.empty;
        render();
      }
    } catch (e) {
      console.error(e);
      if (!launches.length) grid.innerHTML = `<div class="empty">Could not load wells: ${esc(describeError(e))}</div>`;
    }
  }

  load();
  const loop = () => {
    pollTimer = setTimeout(async () => {
      if (!document.hidden) await load();
      if (!stopped) loop();
    }, 10_000);
  };
  loop();
  const badgeTimer = setInterval(tickBadges, 1000);

  return () => {
    stopped = true;
    clearTimeout(pollTimer);
    clearInterval(badgeTimer);
    scene.destroy();
    sim.destroy();
    offParallax();
  };
}

const X_URL = "https://x.com/WaterWellSol";
const X_ICON = `<svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path fill="currentColor" d="M17.75 3h3.07l-6.7 7.66L22 21h-6.17l-4.83-6.32L5.47 21H2.4l7.17-8.2L2 3h6.32l4.37 5.78L17.75 3Zm-1.08 16.17h1.7L7.4 4.74H5.58l11.09 14.43Z"/></svg>`;

function emptyStateHtml(mode) {
  const copy = {
    fresh: "The program is live on Solana and waiting for its first launch. Dig the first well, or follow us on X to catch it the moment it opens.",
    older: "Wells from an earlier program version are hidden. The current program is live and waiting for its first launch.",
    soon: "Launches are not open on this network yet. Follow us on X to catch the first well the moment it opens.",
  }[mode];
  return `
  <div class="first-well">
    <svg class="dry-well" viewBox="0 0 160 150" aria-hidden="true">
      <defs>
        <linearGradient id="dw-body" x1="0" x2="1"><stop offset="0" stop-color="#070a12"/><stop offset=".45" stop-color="#232b40"/><stop offset="1" stop-color="#06080e"/></linearGradient>
        <radialGradient id="dw-glow" cx="50%" cy="50%" r="50%"><stop offset="0" stop-color="#00e5ff" stop-opacity=".35"/><stop offset="1" stop-color="#00e5ff" stop-opacity="0"/></radialGradient>
      </defs>
      <ellipse cx="80" cy="132" rx="70" ry="12" fill="url(#dw-glow)"/>
      <ellipse class="dw-ring" cx="80" cy="132" rx="58" ry="9" fill="none" stroke="#00e5ff" stroke-opacity=".35" stroke-dasharray="3 6"/>
      <path d="M38 86v36c0 7 19 12 42 12s42-5 42-12V86" fill="url(#dw-body)" stroke="#3a4560" stroke-width="1.5"/>
      <ellipse cx="80" cy="86" rx="44" ry="13" fill="#2c3549" stroke="#4a5670" stroke-width="1.5"/>
      <ellipse cx="80" cy="86" rx="34" ry="9" fill="#04060b" stroke="#00e5ff" stroke-opacity=".55"/>
      <ellipse cx="80" cy="89" rx="20" ry="4" fill="#00e5ff" fill-opacity=".08"/>
      <path class="dw-drop" d="M80 18c-5 8-8 12.5-8 16.5a8 8 0 0 0 16 0C88 30.5 85 26 80 18z" fill="#00e5ff"/>
    </svg>
    <h3 class="first-title">The first well hasn't been dug yet</h3>
    <p class="first-copy">${copy}</p>
    <div class="first-cta">
      ${mode === "soon" ? "" : `<a class="btn btn-primary" href="#/create">Dig the first well</a>`}
      <a class="btn btn-ghost" href="${X_URL}" target="_blank" rel="noopener">${X_ICON}Follow @WaterWellSol</a>
    </div>
  </div>`;
}

function heroParallax(stage) {
  if (!stage || prefersReducedMotion()) return () => {};
  let raf = 0;
  const onScroll = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      const y = Math.min(window.scrollY, 900);
      stage.style.transform = `translate3d(0, ${(y * 0.14).toFixed(1)}px, 0)`;
      stage.style.opacity = String(Math.max(0.25, 1 - y / 1100));
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  return () => {
    window.removeEventListener("scroll", onScroll);
    cancelAnimationFrame(raf);
  };
}

// ---------------------------------------------------------------- DEMO / SIMULATION section (never mixed with real data)
function mountSim(root) {
  const canvas = $(".sim-canvas", root);
  const ring = $("#sim-ring", root);
  const feeNow = $("#sim-fee-now", root);
  const feeSub = $("#sim-fee-sub", root);
  const feeBox = $("#sim-fee", root);
  const dot = $("#sim-curve-dot", root);
  const now = $("#sim-curve-now", root);
  const log = $("#sim-log", root);
  const el = { level: $("#sim-level", root), poured: $("#sim-poured", root), drunk: $("#sim-drunk", root), trades: $("#sim-trades", root) };
  const curveY = (fee) => 6 + ((SIM.feeStart - fee) / (SIM.feeStart - SIM.feeEnd)) * 44;

  const addLog = (html, cls = "") => {
    const li = document.createElement("li");
    li.className = cls;
    li.innerHTML = html;
    log.prepend(li);
    while (log.children.length > 4) log.lastChild.remove();
  };

  const sim = new DemoSim(canvas, {
    onTick(s) {
      const left = Math.max(0, 1 - s.t / SIM.decay);
      ring.style.strokeDashoffset = String(100 - left * 100);
      feeNow.textContent = `${s.fee.toFixed(1)}%`;
      feeBox.classList.toggle("hot", s.t < SIM.decay && !s.emptying);
      feeSub.textContent = s.emptying
        ? "Restarting the simulation…"
        : s.t < SIM.decay
          ? `${Math.floor(s.t)} s after a simulated launch · falls to 4% at 60 s`
          : `Decay over · normal fee (${SIM.platform}% platform, ${SIM.feeEnd - SIM.platform}% to the well)`;
      const x = (200 * Math.min(s.t, SIM.loop)) / SIM.loop;
      dot.setAttribute("cx", x.toFixed(1));
      dot.setAttribute("cy", curveY(s.fee).toFixed(1));
      now.setAttribute("x1", x.toFixed(1));
      now.setAttribute("x2", x.toFixed(1));
      el.level.textContent = s.reservoir.toFixed(3);
      el.poured.textContent = s.poured.toFixed(3);
      el.drunk.textContent = s.drunk.toFixed(3);
      el.trades.textContent = String(s.trades);
    },
    onTrade(t) {
      addLog(
        `<span class="side ${t.buy ? "b" : "s"}">${t.buy ? "Sim buy" : "Sim sell"}</span><span class="mono">${t.size.toFixed(2)}</span><span class="mono ${t.heat > 0.3 ? "warn" : "muted"}">fee ${t.fee.toFixed(1)}%</span><span class="mono cyan">+${t.toWell.toFixed(3)}</span>`,
        t.heat > 0.3 ? "hot" : ""
      );
    },
    onRestart() {
      log.innerHTML = "";
      addLog(`<span class="restart">New simulated launch: the fee resets to 15%</span>`);
    },
  });
  addLog(`<span class="restart">Simulated launch: the fee starts at 15%</span>`);
  return sim;
}
